import React, { useState, useEffect, useMemo } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "./firebase";
import LoginPage from "./Authentication/Login";
import ShortnerAI from "./Shortener/App";
import ErrorBoundary from "./Boundary";
import axios from "axios";

// --- AI Kernel Imports ---
import { useKernel } from "./Shortener/hooks/AIKernel";
import { useFrustrationSensor } from "./Shortener/hooks/useFrustrationSensor";

// MUI imports
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Alert,
  Chip
} from "@mui/material";
import { Menu, Close } from "@mui/icons-material";

// Framer Motion
import { motion, AnimatePresence } from "framer-motion";

// 🌐 Backend base URL
const API_BASE = "https://apiv1.harishacrypto.me/service5/v1";

export default function Dashboard() {
  // --- AI Engine Integration ---
  const { agentState, reportBehavior } = useKernel();
  
  // Activate global frustration tracking across the entire dashboard
  useFrustrationSensor(); 

  // --- Standard UI State ---
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [activePage, setActivePage] = useState("dashboard");

  // --- Enterprise Billing State ---
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [trialActive, setTrialActive] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [paypalPlan, setPaypalPlan] = useState(null);
  const [billingDetails, setBillingDetails] = useState(null);

  // ✅ Axios instance (CORS-safe config)
  const api = useMemo(() => axios.create({
    baseURL: API_BASE,
    headers: { "Content-Type": "application/json" },
    withCredentials: false, 
  }), []);

  // 1️⃣ Intercept PayPal Redirects on Mount (Post-Checkout Resolution)
  useEffect(() => {
    const resolvePayment = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const subId = urlParams.get("subscription_id");
      const isSuccess = urlParams.get("payment_success");
      const isCancel = urlParams.get("payment_cancel");

      if (subId) {
        setCheckingSubscription(true);
        try {
          if (isSuccess) {
            await api.post("/success", { paypalSubscriptionId: subId });
          } else if (isCancel) {
            await api.post("/cancel-return", { paypalSubscriptionId: subId });
          }
        } catch (err) {
          console.error("Failed to resolve payment:", err);
        }
        // Clean up the URL so it looks nice for the user
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };
    resolvePayment();
  }, [api]);

  // 2️⃣ Auth listener & Subscription Check
  useEffect(() => {
    const checkSubscription = async (email) => {
      try {
        setCheckingSubscription(true);
        const res = await api.post("/check", {
          projectSlug: "tradingking",
          email,
        });

        if (res.data.hasAccess) {
          setSubscriptionStatus("active");
          setTrialActive(res.data.isTrial);
          setBillingDetails(res.data.billing); 
        } else {
          setSubscriptionStatus("inactive");
        }
      } catch (err) {
        console.error("❌ Subscription check failed:", err.message);
        setSubscriptionStatus("inactive");
      } finally {
        setCheckingSubscription(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        // 🧠 AI Sensor: Log successful login/session start
        reportBehavior('session_started', 1, 'behavior');
        await checkSubscription(currentUser.email);
      }
    });
    return () => unsubscribe();
  }, [api, reportBehavior]);

  // 3️⃣ Fetch plans
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/plans", { params: { projectSlug: "tradingking" } });
        const plansArray = Array.isArray(res.data) ? res.data : res.data.data;
        const plan = plansArray?.find((p) => p.slug === "basic");
        setPaypalPlan(plan);
      } catch (e) {
        console.error("Failed to load plans", e);
      }
    })();
  }, [api]);

  // --- Interaction Handlers ---
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    // 🧠 AI Sensor: Track sidebar interaction
    reportBehavior('sidebar_toggled', 0.5, 'behavior');
  };

  const handleSignOut = async () => {
    try {
      // 🧠 AI Sensor: Log intentional session termination
      reportBehavior('session_terminated', 1, 'behavior');
      await signOut(auth);
      setUser(null);
    } catch (err) {
      console.error("Sign-out error:", err);
    }
  };

  const createSubscription = async () => {
    try {
      const baseUrl = window.location.origin;
      const res = await api.post("/create-subscription", {
        projectSlug: "tradingking",
        planSlug: "basic",
        userEmail: user.email,
        subscriberName: user.email.split("@")[0],
        returnUrl: `${baseUrl}/?payment_success=true`,
        cancelUrl: `${baseUrl}/?payment_cancel=true`
      });

      const link = res.data.approveLink;
      if (link) {
        window.location.href = link; 
      } else {
        alert("⚠️ Unable to initiate subscription — try again.");
      }
    } catch (err) {
      console.error("Payment creation failed:", err.message);
      alert("Error while creating subscription.");
    }
  };

  // --- Adaptive MUI Theme ---
  const isDarkMode = agentState.theme === "theme-cinematic-dark";
  const theme = useMemo(() => createTheme({
      palette: {
        mode: isDarkMode ? "dark" : "light",
        primary: { main: "#d7b879" },
        background: { 
          default: isDarkMode ? "#0b0c0f" : "#f5f3ef", 
          paper: isDarkMode ? "#14161b" : "#fffefa" 
        },
      },
      shape: { borderRadius: 16 },
  }), [isDarkMode]);

  // --- Animation Actuator ---
  const transitionDuration = agentState.ui.simplifyAnimations ? 0 : 0.5;
  const sidebarTransition = agentState.ui.simplifyAnimations 
      ? { duration: 0 } 
      : { type: "spring", stiffness: 300, damping: 30 };

  // --- Render Fallbacks ---
  if (!user) return <LoginPage />;

  if (checkingSubscription) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", bgcolor: "background.default" }}>
          <Typography variant="h6" color="text.secondary">Checking your secure connection...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  // ❌ Not subscribed UI (MUI Themed)
  if (subscriptionStatus === "inactive") {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", bgcolor: "background.default", p: 3 }}>
          <Paper elevation={6} sx={{ p: 5, maxWidth: 450, textAlign: "center", borderRadius: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Subscribe to Video Editot Global Pro
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Unlock full Video Editor Global management and optimization features powered by AI.
            </Typography>

            <Typography variant="h4" color="primary" fontWeight="bold" sx={{ mb: 3 }}>
              ${paypalPlan?.price || "25.00"} <Typography component="span" variant="h6" color="text.secondary">/ {paypalPlan?.interval || "MONTH"}</Typography>
            </Typography>

            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={createSubscription}
              sx={{ py: 1.5, fontSize: "1.1rem", fontWeight: "bold" }}
            >
              {paypalPlan?.trialDays > 0 ? `Start ${paypalPlan.trialDays}-Day Free Trial` : "Subscribe with PayPal"}
            </Button>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 3 }}>
              Secure billing provided by PayPal. Cancel anytime.
            </Typography>
          </Paper>
        </Box>
      </ThemeProvider>
    );
  }

  // Content rendering based on active page
  const renderContent = () => {
    switch (activePage) {
      case "dashboard":
        return (
          <ErrorBoundary>
            <ShortnerAI  />
          </ErrorBoundary>
        );
      case "routes":
      case "analytics":
      case "settings":
        return (
          <ErrorBoundary>
            <Typography>⚙️ This Page Content is Under Construction. See you Soon!</Typography>
          </ErrorBoundary>
        );
      default:
        return <Typography>Welcome to Video Editor Global</Typography>;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
        {/* Sidebar */}
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={sidebarTransition}
              style={{ width: 240, zIndex: 1200 }}
            >
              <Paper elevation={6} sx={{ height: "100%", p: 2 }}>
                <List>
                  {["dashboard", "routes", "analytics", "settings"].map((page) => (
                    <ListItemButton
                      key={page}
                      selected={activePage === page}
                      onClick={() => {
                        setActivePage(page);
                        setIsSidebarOpen(false);
                        // 🧠 AI Sensor: Track specific feature interest
                        reportBehavior(`Menu_${page}_accessed`, 2, 'interest');
                      }}
                    >
                      <ListItemText primary={page.charAt(0).toUpperCase() + page.slice(1)} />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Overlay */}
        <AnimatePresence>
            {isSidebarOpen && (
            <Box
                component={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.5 }}
                exit={{ opacity: 0 }}
                transition={{ duration: transitionDuration }}
                onClick={toggleSidebar}
                sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                bgcolor: "black",
                zIndex: 1100,
                }}
            />
            )}
        </AnimatePresence>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Navbar */}
          <Paper
            elevation={3}
            sx={{
              p: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: "sticky",
              top: 0,
              zIndex: 1000,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                edge="start"
                onClick={toggleSidebar}
                sx={{ mr: 1, display: { lg: "none" } }}
              >
                {isSidebarOpen ? <Close /> : <Menu />}
              </IconButton>
              <Typography variant="h6" fontWeight="bold">TradingKing</Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>{user?.email}</Typography>
              <Button 
                variant={agentState.ui.highlightCTAs ? "contained" : "outlined"} 
                color="primary" 
                onClick={handleSignOut}
              >
                Sign Out
              </Button>
            </Box>
          </Paper>

          {/* Main Content */}
          <Box sx={{ p: { xs: 2, md: 4 }, flex: 1 }}>
            
            {/* Grace Period Banner ported to MUI */}
            {billingDetails?.cancelAtPeriodEnd && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                Your subscription is cancelled, but you have access until the end of your billing period.
              </Alert>
            )}

            <AnimatePresence mode="wait">
                <motion.div
                key={activePage}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: transitionDuration }}
                >
                <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h5" fontWeight="bold">
                        Linkpin Control Panel
                      </Typography>
                      <Chip 
                        label={trialActive ? "Trial Mode" : "Pro"} 
                        color="primary" 
                        variant={trialActive ? "outlined" : "filled"}
                        size="small"
                        sx={{ ml: 2, fontWeight: 'bold' }} 
                      />
                    </Box>
                    {renderContent()}
                </Paper>
                </motion.div>
            </AnimatePresence>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}